/**
 * The frame.
 *
 *   geometry prepass (depth + view normal + roughness/metal + motion vectors)
 *     -> GTAO (half res, blue noise, bilateral upsample)
 *     -> forward HDR pass, with AO folded into indirect diffuse and last frame's
 *        SSR blended over the probe radiance inside the BRDF
 *     -> volumetric composite
 *     -> SSR raymarch (feeds the next frame)
 *     -> viewmodel prepass + viewmodel colour on a cleared depth buffer
 *     -> TAA (or SMAA on the low preset) + RCAS
 *     -> motion blur -> bokeh DOF
 *     -> histogram auto exposure -> bloom
 *     -> AgX + filmic grade + lens + grain + dither -> screen
 *
 * owner: render (this file and everything else under src/render/)
 */
import * as THREE from 'three';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { registerShot, look } from '../core/Shots.js';
import { TOD } from '../world/Sky.js';
import { perlin2, clamp } from '../core/Rand.js';
import {
  Blitter, makeRT, haltonJitterTable, makeBlueNoiseTexture, makeLensDirtTexture, makeFlatTexture,
} from './Common.js';
import { makeMaterial, GLSL_COMMON } from './Common.js';
import { Prepass } from './Prepass.js';
import { GTAO } from './GTAO.js';
import { SSR } from './SSR.js';
import { TAA } from './TAA.js';
import { Bloom } from './Bloom.js';
import { DOF } from './DOF.js';
import { MotionBlur } from './MotionBlur.js';
import { VolumetricComposite, AutoExposure, Composite } from './Composite.js';
import { ProbeRig, PROBE_LAYER } from './ProbeRig.js';

/* ------------------------------------------------------------------ *
 * Quality presets
 * ------------------------------------------------------------------ */

/* `lamps` is the practical-light budget (three has no light clustering, so every
 * visible point light is evaluated in every fragment — measured 84 % of an M4's
 * frame at 42 lamps). `dpr` caps the device pixel ratio; it is skipped when the
 * page was opened with an explicit ?dpr= (the capture harness pins dpr=1). */
const PRESETS = {
  ultra: {
    scale: 1.0, gtao: true, gtaoSlices: 3, gtaoSteps: 6, contact: true, csSteps: 12,
    ssr: true, ssrSteps: 32, ssrRefine: 5,
    taa: true, smaa: false, bloomLevels: 7, streak: true,
    dof: true, dofTaps: 40, mblur: true, mbTaps: 12, volumetric: true,
    lamps: Infinity, dpr: 1.5,
  },
  high: {
    scale: 1.0, gtao: true, gtaoSlices: 2, gtaoSteps: 5, contact: true, csSteps: 10,
    ssr: true, ssrSteps: 20, ssrRefine: 4,
    taa: true, smaa: false, bloomLevels: 6, streak: true,
    dof: true, dofTaps: 24, mblur: true, mbTaps: 8, volumetric: true,
    lamps: 16, dpr: 1.25,
  },
  medium: {
    scale: 0.85, gtao: true, gtaoSlices: 2, gtaoSteps: 4, contact: true, csSteps: 8,
    ssr: false, ssrSteps: 12, ssrRefine: 3,
    taa: true, smaa: false, bloomLevels: 5, streak: false,
    dof: false, dofTaps: 16, mblur: true, mbTaps: 6, volumetric: true,
    lamps: 8, dpr: 1.0,
  },
  low: {
    scale: 0.75, gtao: false, gtaoSlices: 2, gtaoSteps: 3, contact: false, csSteps: 6,
    ssr: false, ssrSteps: 8, ssrRefine: 2,
    taa: false, smaa: true, bloomLevels: 4, streak: false,
    dof: false, dofTaps: 12, mblur: false, mbTaps: 4, volumetric: true,
    lamps: 4, dpr: 1.0,
  },
  potato: {
    scale: 0.6, gtao: false, gtaoSlices: 2, gtaoSteps: 3, contact: false, csSteps: 6,
    ssr: false, ssrSteps: 8, ssrRefine: 2,
    taa: false, smaa: true, bloomLevels: 3, streak: false,
    dof: false, dofTaps: 12, mblur: false, mbTaps: 4, volumetric: false,
    lamps: 0, dpr: 0.85, matTier: 'lite',
  },
};

/* ------------------------------------------------------------------ *
 * Forward-pass injection: AO on indirect diffuse, SSR over probe radiance
 * ------------------------------------------------------------------ */

const MATERIAL_UNIFORM_DECL = /* glsl */`
uniform sampler2D postfxAO;
uniform sampler2D postfxSSR;
uniform vec2 postfxTexel;
uniform vec4 postfxFlags;
uniform vec4 postfxFlags2;
`;

const MATERIAL_INJECT = /* glsl */`
#if defined( RE_IndirectDiffuse ) && defined( RE_IndirectSpecular )
{
  vec2 pfUv = gl_FragCoord.xy * postfxTexel;
  vec4 pfOcc = texture2D( postfxAO, pfUv );
  float pfAO = postfxFlags.x > 0.5 ? pfOcc.r : 1.0;
  irradiance *= pfAO;
  iblIrradiance *= pfAO;
  float pfNV = clamp( dot( geometryNormal, geometryViewDir ), 0.0, 1.0 );
  float pfSO = clamp( pow( pfNV + pfAO, exp2( -16.0 * material.roughness - 1.0 ) ) - 1.0 + pfAO, 0.0, 1.0 );
  radiance *= pfSO;
  if ( postfxFlags.y > 0.5 ) {
    vec4 pfSSR = texture2D( postfxSSR, pfUv );
    radiance = mix( radiance, pfSSR.rgb, clamp( pfSSR.a * postfxFlags.z, 0.0, 1.0 ) );
  }
  /* Contact darkening on DIRECT light.
     Occlusion strictly belongs on the indirect term, and that is where the bulk
     of it stays — but the one job this pass exists to do is put a dark line where
     a wall meets the ground, and in a sunlit street the indirect term is a small
     fraction of the pixel, so a physically pure AO is invisible exactly where it
     matters most. Every shipped renderer cheats here. The cheat is bounded: it
     only engages below AO 0.9, and at a fraction of full strength, so it reads as
     a contact shadow and never as a grey wash over lit geometry. */
  float pfContact = mix( 1.0, pfAO, postfxFlags.w * smoothstep( 0.92, 0.28, pfAO ) );
  /* The real thing: a ray-marched screen-space contact shadow (GTAO.js). The
     cheat above is derived from a hemisphere integral and cannot know where the
     sun is; this one does, so it puts the shadow on the correct side of the
     object and hardens toward the point of contact. Direct light only — an
     occluder 3 cm away blocks the sun, not the sky. */
  float pfCS = mix( 1.0, pfOcc.g, postfxFlags2.x );
  reflectedLight.directDiffuse *= pfContact * pfCS;
  reflectedLight.directSpecular *= mix( 1.0, pfContact, 0.5 ) * mix( 1.0, pfCS, 0.85 );
}
#endif
`;

/* The viewmodel gets its own, deliberately smaller injection.
 *
 * It is shaded in a second forward pass off its own G-buffer, so it needs its
 * own occlusion buffer — and it must not carry the SSR sampler, because the
 * weapon materials are the most sampler-hungry in the build and the fragment
 * stage has 16 image units total (FINDINGS F6). One sampler, no SSR. */
const VIEWMODEL_UNIFORM_DECL = /* glsl */`
uniform sampler2D postfxAO;
uniform vec2 postfxTexel;
uniform vec4 postfxFlags2;
`;

const VIEWMODEL_INJECT = /* glsl */`
#if defined( RE_IndirectDiffuse )
{
  vec4 pfOcc = texture2D( postfxAO, gl_FragCoord.xy * postfxTexel );
  float pfAO = mix( 1.0, pfOcc.r, postfxFlags2.z );
  irradiance *= pfAO;
  iblIrradiance *= pfAO;
  float pfCS = mix( 1.0, pfOcc.g, postfxFlags2.y );
  reflectedLight.directDiffuse *= pfCS;
  reflectedLight.directSpecular *= mix( 1.0, pfCS, 0.85 );
}
#endif
`;

const DEBUG_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tDebug;
uniform int uMode;
uniform float uScale;
varying vec2 vUv;
void main() {
  vec4 s = texture2D(tDebug, vUv);
  vec3 c;
  if (uMode == 1) c = vec3(s.r);                       // ao
  else if (uMode == 2) c = s.rgb * uScale;             // ssr colour
  else if (uMode == 3) c = vec3(s.a);                  // ssr confidence
  else if (uMode == 4) c = s.rgb * 0.5 + 0.5;          // view normal
  else if (uMode == 5) c = vec3(abs(s.r) * 60.0, abs(s.g) * 60.0, 0.0); // velocity
  else if (uMode == 6) c = vec3(s.b * 0.02);           // linear depth
  else if (uMode == 7) c = vec3(max(s.a, 0.0));        // flags
  else c = s.rgb * uScale;
  gl_FragColor = vec4(srgbEncode(clamp(c, 0.0, 1.0)), 1.0);
}
`;

const _euler = new THREE.Euler();
const _vec = new THREE.Vector3();

/**
 * How far the histogram auto-exposure may stray from the calibrated anchor.
 * +/- 1.5 stops: enough for a doorway, not enough to hide an unlit scene.
 *
 * The anchor is `ctx.lighting.suggestedExposure`, and since it is now applied to
 * the frame as a pre-exposure (see `_baseExposure` below) this window is a
 * window on the *residual*: 1.0 means the frame came out exactly where the
 * lighting model predicted, and railing at 0.354 / 2.828 means the model and the
 * renderer disagree by more than 1.5 stops, which is a bug in one of them.
 */
export const AE_STOPS = 1.5;
const AE_RANGE = Math.pow(2, AE_STOPS);

/**
 * The AE key used until lighting publishes one. Mirrors Sky.EXPOSURE_KEY; it is
 * duplicated rather than imported so `src/render/**` keeps no dependency on
 * `src/world/**`.
 */
const DEFAULT_EXPOSURE_KEY = 0.225;

/* ------------------------------------------------------------------ */

class RenderSystem {
  constructor() {
    this.name = 'render';
    this.priority = 1000;
    this.ownsRender = true;

    this.preset = 'ultra';
    this.opts = PRESETS.ultra;

    this.blit = new Blitter();
    this.prepass = new Prepass();
    this.gtao = new GTAO(this.blit);
    this.ssr = new SSR(this.blit);
    this.taa = new TAA(this.blit);
    this.bloom = new Bloom(this.blit);
    this.dof = new DOF(this.blit);
    this.motionBlur = new MotionBlur(this.blit);
    this.volumetric = new VolumetricComposite(this.blit);
    this.exposure = new AutoExposure(this.blit);
    this.composite = new Composite(this.blit);
    this.smaa = null;

    this.hdrRT = null;
    this.smaaRT = null;
    this.width = 1; this.height = 1;
    this.canvasWidth = 1; this.canvasHeight = 1;

    this.jitterTable = haltonJitterTable(8);
    this.frameIndex = 0;

    // shared uniform objects handed to every patched scene material
    this.uAO = { value: null };
    this.uSSR = { value: null };
    this.uTexel = { value: new THREE.Vector2(1, 1) };
    this.uFlags = { value: new THREE.Vector4(0, 0, 1, 0.45) };
    /* x = world contact-shadow strength, y = viewmodel contact-shadow strength,
       z = viewmodel AO strength, w = spare. Kept out of `uFlags` because that
       vector is full and a fifth component is a uniform, not a texture unit. */
    this.uFlags2 = { value: new THREE.Vector4(0, 0, 0, 0) };
    this._patched = new Set();

    /* Contact shadows. 0.35 m is the middle of the 0.2-0.5 m band a contact
       shadow has to cover: below it the sun cascade cannot resolve anything
       (its texel is ~2 cm in the nearest cascade), above it the cascade is
       already correct and a screen-space ray only adds artefacts. */
    this.contact = {
      strength: 0.80, length: 0.35, thickness: 0.60, bias: 0.014,
      fadeStart: 24, fadeEnd: 48,
    };
    /* A weapon is a 60 cm object seen from 30 cm. Everything scales down: the
       gap between an optic and the receiver under it is ~2 cm, and a 35 cm ray
       would march straight past the whole gun into the world behind it. */
    this.contactVm = {
      strength: 0.85, length: 0.055, thickness: 0.05, bias: 0.0018,
      fadeStart: 40, fadeEnd: 80,
    };
    this._lightVS = new THREE.Vector3(0, 1, 0);
    this._projView = new THREE.Matrix4();

    // transient effects
    this._shakes = [];
    this._flash = { color: new THREE.Color(1, 1, 1), power: 0, decay: 1 };
    this._damage = 0;
    this._damageDecay = 1.1;
    this._manualExposure = 0;
    /**
     * The exposure anchor, published by lighting. Applied to the frame *before*
     * the histogram meters it and before the tonemapper, so what the histogram
     * settles on is the residual correction and not the whole exposure. 1.0 is
     * only the value used before lighting has published anything.
     */
    this._baseExposure = 1;
    this._exposureKey = DEFAULT_EXPOSURE_KEY;
    this._forceConverge = false;
    this._lastColorTex = null;
    this._rawMode = false;
    this.noiseSize = 64;

    // matrices
    this._projSaved = new THREE.Matrix4();
    this._projSavedView = new THREE.Matrix4();
    this._curVP = new THREE.Matrix4();
    this._prevVP = new THREE.Matrix4();
    this._curVPView = new THREE.Matrix4();
    this._prevVPView = new THREE.Matrix4();
    this._invProj = new THREE.Matrix4();
    this._proj = new THREE.Matrix4();
    this._invProjView = new THREE.Matrix4();
    this._tmpM = new THREE.Matrix4();

    this._prevCamPos = new THREE.Vector3();
    this._prevCamQuat = new THREE.Quaternion();
    this._hasPrev = false;

    this._savedPos = new THREE.Vector3();
    this._savedQuat = new THREE.Quaternion();
    this._savedPosView = new THREE.Vector3();
    this._savedQuatView = new THREE.Quaternion();

    this.rig = new ProbeRig();
    this._rigArmedFrame = -99;
    this._forcedCam = null;
    this._hasBaseExposure = false;

    // shadow-map integrity guard (see _ensureShadowMaps)
    this._shadowScanFrame = -999;
    this._shadowAttempts = new WeakMap();
    this._shadowMissing = 0;

    this.volumetricIntensity = 1.0;
    this.debugView = 'none';
    this.debugMat = makeMaterial({
      name: 'postfx.debug',
      fragmentShader: DEBUG_FRAG,
      uniforms: { tDebug: { value: null }, uMode: { value: 0 }, uScale: { value: 1 } },
    });

    this.stats = { cpuMs: 0 };
    /** > 0 pins the focal plane; 0 leaves it to the G-buffer autofocus. */
    this._focusOverride = 0;
    this._ads = 0;
    this._adsOverride = null;
  }

  /* ---------------------------------------------------------------- */

  init(ctx) {
    const self = this;
    const r = ctx.renderer;
    r.toneMapping = THREE.NoToneMapping;
    r.toneMappingExposure = 1.0;
    r.setClearColor(0x000000, 1);

    const supplied = ctx.materials && ctx.materials.blueNoise;
    this.blueNoise = (supplied && supplied.isTexture) ? supplied : makeBlueNoiseTexture(64);
    const img = this.blueNoise.image || {};
    this.noiseSize = img.width || 64;
    this.lensDirt = makeLensDirtTexture(512);
    this.whiteTex = makeFlatTexture(255, 255, 255, 255);
    this.zeroTex = makeFlatTexture(0, 0, 0, 0);
    this.uAO.value = this.whiteTex;
    this.uSSR.value = this.zeroTex;

    this.setQuality(ctx.quality || ctx.config?.quality || 'ultra', ctx);
    this._ensureSize(ctx);

    this.rig.build(ctx);
    this.rig.buildSsrProbe(ctx);
    /* Build the probe environment now rather than on first arm. It allocates a
       DataTexture and a PMREM target, and doing that mid-run makes the capture
       harness's resource-leak gate fire on whichever shot happened to arm the
       rig first (+4 textures on `post_probe`). Paying it at boot is honest. */
    this.rig.ensureEnv(r);

    // Public API (CONTRACTS.md :: ctx.render)
    const api = {
      shake: (power, duration) => this.shake(power, duration),
      flash: (color, power, duration) => this.flash(color, power, duration),
      damageVignette: (power) => this.damageVignette(power),
      setDamageVignette: (power) => this.setDamageVignette(power),
      setExposure: (v, key) => this.setExposure(v, key),
      setQuality: (preset) => this.setQuality(preset, ctx),
      passes: {
        prepass: this.prepass, gtao: this.gtao, ssr: this.ssr, taa: this.taa,
        bloom: this.bloom, dof: this.dof, motionBlur: this.motionBlur,
        volumetric: this.volumetric, exposure: this.exposure, composite: this.composite,
      },
      composer: {
        passes: [this.prepass, this.gtao, this.ssr, this.taa, this.motionBlur, this.dof, this.bloom, this.composite],
        render: () => this.render(ctx),
        setSize: (w, h) => this.resize(w, h, ctx),
        /* `this` inside an object literal getter is the literal, not the render
           system, so this used to return undefined and no tool could read the
           HDR buffer back. It is the only place a measurement can see scene
           radiance before the tonemapper touches it — FINDINGS F13 was argued
           for a whole session without it. */
        get renderTarget() { return self.hdrRT; },
      },
      /** The scene's HDR buffer, in scene units, pre-exposure and pre-tonemap. */
      get hdrTarget() { return self.hdrRT; },
      // tuning surface
      params: this.composite.mat.uniforms,
      stats: this.stats,
      resetHistory: () => { this.taa.resetHistory(); this.exposure.requestSnap(); },
      /**
       * Land auto-exposure on its histogram target immediately instead of
       * easing toward it over ~1 s of eye adaptation. The capture harness
       * calls this (implicitly, via deterministic mode) so a shot is never
       * photographed mid-adaptation; call it explicitly after teleporting the
       * camera or changing the time of day.
       */
      forceExposureConverge: () => {
        this._forceConverge = true;
        this.exposure.requestSnap();
        if (this._lastColorTex) {
          this.exposure.render(ctx.renderer, {
            colorTexture: this._lastColorTex,
            srcWidth: this.width,
            srcHeight: this.height,
            dt: 1e6,
            manual: this._manualExposure,
            preExposure: this._baseExposure,
            key: this._exposureKey,
            minExposure: 1 / AE_RANGE,
            maxExposure: AE_RANGE,
            snap: true,
          });
          ctx.renderer.setRenderTarget(null);
        }
      },
      setRawMode: (on) => { this._rawMode = !!on; },
      /**
       * Pin the focal plane, in metres. 0 (the default) hands it back to the
       * G-buffer autofocus, which meters the centre of the frame every frame.
       * Any team staging a hero shot can call this to compose with the lens —
       * a soft foreground occluder, a sharp subject, a soft background is what
       * three of the four best-scoring shipped frames in refs/ are built on.
       */
      setFocus: (metres) => { this._focusOverride = (metres > 0) ? metres : 0; this.dof.requestSnap(); },
      /** The distance the autofocus actually settled on. One GPU readback — do not call per frame. */
      readFocus: () => this.dof.readFocus(ctx.renderer),
      /**
       * Screen-space contact shadows. `world` covers the 0.2-0.5 m band the sun
       * cascade cannot resolve; `viewmodel` is the same effect at weapon scale.
       * Set `strength` to 0 to disable either.
       */
      contactShadows: this.contact,
      contactShadowsViewmodel: this.contactVm,
      probeRig: this.rig,
      /**
       * Hold the render camera at an exact pose for the capture harness.
       * The player system owns ctx.camera and rewrites it every tick, so a shot
       * that only calls look() gets walked back before the frame is drawn.
       * Any team's shot can call this instead; the pin is released automatically
       * as soon as another shot moves the player.
       */
      pinCamera: (from, at, fov = 74) => {
        const p = ctx.get && ctx.get('player');
        if (p && typeof p.freeze === 'function') p.freeze(true);
        look(ctx, from, at, fov);
        this._pin(ctx, fov);
        this._rigArmedFrame = ctx.time.frame;
        this.taa.resetHistory();
        this.exposure.requestSnap();
      },
      unpinCamera: () => this._releaseShot(ctx),
      /** 'none' | 'ao' | 'ssr' | 'ssrconf' | 'normal' | 'velocity' | 'depth' | 'flags' | 'bloom' | 'hdr' */
      setDebugView: (v) => { this.debugView = v || 'none'; },
      /** Multiplier on ctx.lighting.volumetricTexture when we composite it. */
      set volumetricIntensity(v) { self.volumetricIntensity = v; },
      get volumetricIntensity() { return self.volumetricIntensity; },
      quality: this.preset,
      /* flags other systems look for */
      // Render owns the final exposure value; lighting owns the anchor it adapts
      // around and must publish it through setExposure(). Nobody outside
      // src/render/** touches renderer.toneMappingExposure.
      ownsExposure: true,
      exposureStops: AE_STOPS,     // adaptation window around the anchor, +/- stops
      compositesVolumetrics: true, // lighting must not draw its own composite quad
      autoExposure: true,
    };
    api.composer.hdrRT = null;
    ctx.render = api;
    this._api = api;

    ctx.events.on('damage:taken', (p) => {
      const amount = (p && p.amount) || 10;
      this.damageVignette(clamp(amount / 30, 0.2, 1.0));
      this.shake(clamp(amount / 90, 0.06, 0.5), 0.28);
    });
    ctx.events.on('fx:explosion', (p) => {
      const power = (p && p.power) || 1;
      let dist = 8;
      if (p && p.position && ctx.camera) dist = Math.max(1.2, ctx.camera.position.distanceTo(p.position));
      const atten = clamp(power * 6 / (dist * dist), 0, 1.4);
      this.flash(0xffd2a0, atten * 0.55, 0.22);
      this.shake(clamp(atten * 0.8, 0, 1.2), 0.55);
    });
    ctx.events.on('player:land', (p) => {
      const impact = (p && p.impact) || 0;
      if (impact > 2) this.shake(clamp(impact / 30, 0.03, 0.4), 0.22);
    });

    this._registerShots(ctx);
  }

  /* ---------------------------------------------------------------- */

  setQuality(preset, ctx) {
    const o = PRESETS[preset] || PRESETS.ultra;
    this.preset = PRESETS[preset] ? preset : 'ultra';
    this.opts = o;
    this.gtao.setQuality(o.gtaoSlices, o.gtaoSteps, o.csSteps);
    this.ssr.setQuality(o.ssrSteps, o.ssrRefine);
    this.dof.setQuality(o.dofTaps);
    this.motionBlur.setQuality(o.mbTaps);
    if (this._api) this._api.quality = this.preset;
    if (ctx) {
      (ctx.lighting || ctx.get?.('lighting'))?.setLampBudget?.(o.lamps ?? Infinity);
      ctx.materials?.setDetailTier?.(o.matTier ?? 'full');
      if (o.dpr && ctx.engine && !ctx.config?.dprLocked) {
        ctx.config.maxPixelRatio = o.dpr;
        ctx.engine._onResize();
      }
    }
    if (ctx) { this._forceResize = true; this._ensureSize(ctx); this.taa.resetHistory(); }
  }

  /**
   * Anchor exposure. The lighting team feeds the physically derived exposure
   * (`ctx.lighting.suggestedExposure`) through here every frame, together with
   * the linear key it expects the pre-exposed frame to meter to.
   *
   * The anchor multiplies the frame; the histogram then corrects it by at most
   * +/- AE_STOPS. That ordering is the whole point: with the anchor as nothing
   * but the centre of a clamp it could not move the image, so auto-exposure was
   * free to rail against the clamp in both directions on different shots and the
   * frames only looked right because it did (FINDINGS F1).
   *
   * Pass 0 to reset to the neutral anchor of 1.0 — with a 1.0 anchor and the
   * default key this behaves exactly like the un-anchored pipeline did.
   */
  setExposure(v, key) {
    const next = (v > 0 && isFinite(v)) ? v : 1;
    const nextKey = (key > 0 && isFinite(key)) ? key : DEFAULT_EXPOSURE_KEY;
    this._hasBaseExposure = (v > 0 && isFinite(v));
    if (next === this._baseExposure && nextKey === this._exposureKey) return;
    /* The anchor now moves a little every frame as the camera does, so snapping
       on any change at all would disable eye adaptation entirely. Snap only when
       it steps — a shot change or a teleport — which is a quarter stop or more. */
    const step = next / Math.max(1e-6, this._baseExposure);
    this._baseExposure = next;
    this._exposureKey = nextKey;
    if (step > 1.19 || step < 0.84) this.exposure.requestSnap();
  }
  /** Hard override, ignores the histogram entirely. 0 = back to automatic. */
  setExposureManual(v) { this._manualExposure = v > 0 ? v : 0; }

  shake(power = 0.3, duration = 0.4) {
    if (power <= 0 || duration <= 0) return;
    this._shakes.push({ power, duration, t: 0, seed: (this._shakes.length * 37 + this.frameIndex * 13) % 997 });
    if (this._shakes.length > 12) this._shakes.shift();
  }

  flash(color = 0xffffff, power = 1, duration = 0.2) {
    const c = color instanceof THREE.Color ? color : new THREE.Color(color);
    if (power >= this._flash.power) this._flash.color.copy(c);
    this._flash.power = Math.max(this._flash.power, power);
    this._flash.decay = 1 / Math.max(0.01, duration);
  }

  /**
   * Request the red damage edge. Monotonic while > 0 (a second hit must not be
   * able to *reduce* the flash from the first), but `damageVignette(0)` — or any
   * non-positive value — clears it immediately. Without that there was no way to
   * clear it at all and a capture run bled a red edge onto every shot that
   * followed a low-health one. `setDamageVignette` is the absolute setter.
   */
  damageVignette(power = 1) {
    if (!(power > 0)) { this._damage = 0; return; }
    this._damage = Math.min(1.2, Math.max(this._damage, power));
  }

  /** Absolute setter — takes the value regardless of what is already showing. */
  setDamageVignette(power = 0) {
    this._damage = (power > 0) ? Math.min(1.2, power) : 0;
  }

  /* ---------------------------------------------------------------- */

  resize(w, h, ctx) {
    this._forceResize = true;
    if (ctx) this._ensureSize(ctx);
  }

  _ensureSize(ctx) {
    const r = ctx.renderer;
    const size = r.getDrawingBufferSize(new THREE.Vector2());
    const cw = Math.max(1, size.x | 0), ch = Math.max(1, size.y | 0);
    const scale = this.opts.scale;
    const w = Math.max(2, Math.round(cw * scale));
    const h = Math.max(2, Math.round(ch * scale));
    if (!this._forceResize && w === this.width && h === this.height && cw === this.canvasWidth) return;
    this._forceResize = false;
    this.canvasWidth = cw; this.canvasHeight = ch;
    this.width = w; this.height = h;

    if (this.hdrRT) this.hdrRT.dispose();
    this.hdrRT = makeRT(w, h, { depthBuffer: true, name: 'postfx.hdr' });

    this.prepass.setSize(w, h);
    this.gtao.setSize(w, h);
    this.ssr.setSize(w, h);
    this.taa.setSize(w, h);
    this.bloom.setSize(w, h, this.opts.bloomLevels);
    this.dof.setSize(w, h);
    this.motionBlur.setSize(w, h);
    this.exposure.setSize(w, h);

    if (this.opts.smaa) {
      if (!this.smaa) this.smaa = new SMAAPass();
      this.smaa.setSize(w, h);
      if (this.smaaRT) this.smaaRT.dispose();
      this.smaaRT = makeRT(w, h, { name: 'postfx.smaa' });
    }

    this.uTexel.value.set(1 / w, 1 / h);
    this.taa.resetHistory();
    this.exposure.requestSnap();
  }

  /* ---------------------------------------------------------------- */

  _patchMaterial(mat, viewmodel) {
    if (!mat || mat.userData.__postfxPatched) return;
    mat.userData.__postfxPatched = true;
    if (!mat.isMeshStandardMaterial) return;
    const self = this;
    const prev = mat.onBeforeCompile;
    const vm = !!viewmodel;
    mat.onBeforeCompile = function (shader, renderer) {
      if (prev) prev.call(this, shader, renderer);
      shader.uniforms.postfxAO = self.uAO;
      shader.uniforms.postfxTexel = self.uTexel;
      shader.uniforms.postfxFlags2 = self.uFlags2;
      if (!vm) {
        shader.uniforms.postfxSSR = self.uSSR;
        shader.uniforms.postfxFlags = self.uFlags;
      }
      shader.fragmentShader = (vm ? VIEWMODEL_UNIFORM_DECL : MATERIAL_UNIFORM_DECL)
        + shader.fragmentShader.replace(
          '#include <lights_fragment_end>',
          (vm ? VIEWMODEL_INJECT : MATERIAL_INJECT) + '\n#include <lights_fragment_end>',
        );
    };
    const key = mat.customProgramCacheKey;
    const tag = vm ? 'postfxvm2|' : 'postfx2|';
    mat.customProgramCacheKey = function () {
      return tag + (key ? key.call(this) : '');
    };
    mat.needsUpdate = true;
    this._patched.add(mat);
  }

  /* ---------------------------------------------------------------- *
   * Shadow-map integrity guard
   *
   * three declares `uniform sampler2DShadow directionalShadowMap[ N ]` (and the
   * spot equivalent) for every *shadow-casting* light in the scene, and uploads
   * `light.shadow.map.depthTexture` into it. If a caster's map has never been
   * rendered — which is exactly what happens the moment a shadow caster becomes
   * visible while `renderer.shadowMap.autoUpdate === false` — three uploads
   * `null`, and `setValueT1Array` falls back to its 1x1 RGBA placeholder.
   *
   * A plain RGBA texture in a shadow sampler is a hard error on ANGLE:
   *   GL_INVALID_OPERATION: Mismatch between texture format and sampler type
   * and an erroring draw is DISCARDED. Every shadow-receiving material in the
   * frame then renders *nothing* — which is what emptied the whole material
   * evidence set while the harness reported zero console errors.
   *
   * Detecting it costs one scene traverse every few frames; missing it costs the
   * entire frame. If a caster still has no map after a few attempts we clear its
   * `castShadow` rather than keep spending a full shadow re-render on it: an
   * unshadowed light is a defect, a discarded draw call is a black frame.
   * ---------------------------------------------------------------- */

  _ensureShadowMaps(ctx, force) {
    const r = ctx.renderer;
    if (r.shadowMap.enabled === false) return;
    /* Scan every frame while something is actually missing. The 4-frame cadence
       is fine as a background sweep, but a caster that appears mid-run — the
       probe rig's sun and lamp are exactly that — must be caught on the frame it
       appears, not up to four frames later, because every one of those frames
       renders with a 1x1 RGBA texture bound to a sampler2DShadow and ANGLE
       discards the draw. */
    if (!force && this._shadowMissing === 0 && this.frameIndex - this._shadowScanFrame < 4) return;
    this._shadowScanFrame = this.frameIndex;

    let missing = 0;
    const attempts = this._shadowAttempts;
    const check = (o) => {
      if (!o.isLight || o.castShadow !== true || !o.shadow) return;
      if (o.shadow.map) { attempts.delete(o); return; }
      const n = (attempts.get(o) || 0) + 1;
      attempts.set(o, n);
      if (n > 3) {
        console.warn('[render] shadow map never rendered for light '
          + `"${o.name || o.type}" — dropping its castShadow so ANGLE stops discarding draws`);
        o.castShadow = false;
        return;
      }
      missing++;
    };
    ctx.scene.traverseVisible(check);
    if (ctx.viewScene) ctx.viewScene.traverseVisible(check);

    this._shadowMissing = missing;
    if (missing > 0) r.shadowMap.needsUpdate = true;
  }

  /* ---------------------------------------------------------------- */

  update(dt, ctx) {
    for (let i = this._shakes.length - 1; i >= 0; i--) {
      const s = this._shakes[i];
      s.t += dt;
      if (s.t >= s.duration) this._shakes.splice(i, 1);
    }
    if (this._flash.power > 0) {
      this._flash.power = Math.max(0, this._flash.power - dt * this._flash.decay * this._flash.power - dt * 0.05);
      if (this._flash.power < 1e-3) this._flash.power = 0;
    }
    if (this._damage > 0) {
      this._damage = Math.max(0, this._damage - dt * this._damageDecay);
    }
    this.rig.syncViewmodelLights(ctx);
  }

  _computeShake(ctx) {
    let px = 0, py = 0, rz = 0, ox = 0, oy = 0;
    const t = ctx.time.elapsed;
    for (const s of this._shakes) {
      const k = 1 - s.t / s.duration;
      const amp = s.power * k * k;
      const f = 26.0;
      px += perlin2(t * f + s.seed, 0.13) * amp * 0.045;
      py += perlin2(t * f + s.seed + 41.7, 3.7) * amp * 0.045;
      rz += perlin2(t * f * 0.7 + s.seed + 91.3, 7.1) * amp * 0.035;
      ox += perlin2(t * f * 0.6 + s.seed + 17.1, 11.3) * amp * 0.035;
      oy += perlin2(t * f * 0.6 + s.seed + 63.9, 19.7) * amp * 0.035;
    }
    return { px, py, rz, ox, oy, active: this._shakes.length > 0 };
  }

  /* ---------------------------------------------------------------- */

  render(ctx) {
    const t0 = (typeof performance !== 'undefined') ? performance.now() : 0;
    const r = ctx.renderer;
    const cam = ctx.camera;
    const vcam = ctx.viewCamera;
    const o = this.opts;
    const raw = this._rawMode;

    this._ensureSize(ctx);
    this.frameIndex++;

    /* ---- transient camera shake (never mutate the player's state) ---- */
    const shake = this._computeShake(ctx);
    this._savedPos.copy(cam.position);
    this._savedQuat.copy(cam.quaternion);
    this._savedPosView.copy(vcam.position);
    this._savedQuatView.copy(vcam.quaternion);
    if (shake.active) {
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(shake.px, shake.py, shake.rz, 'YXZ'));
      cam.quaternion.multiply(q);
      vcam.quaternion.multiply(q);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
      cam.position.addScaledVector(right, shake.ox).addScaledVector(up, shake.oy);
      vcam.position.addScaledVector(right, shake.ox).addScaledVector(up, shake.oy);
    }

    /* ---- shot bookkeeping: look at the pose other systems produced, before we
       override it, so a shot change is still detectable while we hold a camera ---- */
    let jumped = !this._hasPrev;
    if (this._hasPrev) {
      const dp = this._prevCamPos.distanceTo(cam.position);
      const dq = Math.abs(this._prevCamQuat.dot(cam.quaternion));
      if (dp > 1.5 || dq < 0.965) jumped = true;
    }
    this._prevCamPos.copy(cam.position);
    this._prevCamQuat.copy(cam.quaternion);
    this._hasPrev = true;

    if (jumped && this._rigArmedFrame < ctx.time.frame - 1) this._releaseShot(ctx);

    /* Transient screen effects must not bleed from one capture to the next.
       Only in deterministic (capture-harness) mode: in gameplay a fast flick
       also reads as `jumped`, and a hit taken mid-flick must still show. */
    if (jumped && ctx.engine && ctx.engine.deterministic) {
      this._damage = 0;
      this._flash.power = 0;
      this._shakes.length = 0;
    }

    /* ---- a registered render shot pins the camera every frame; the player
       system owns ctx.camera and would otherwise walk it back ---- */
    if (this._forcedCam) {
      const f = this._forcedCam;
      cam.position.copy(f.pos);
      cam.quaternion.copy(f.quat);
      if (cam.fov !== f.fov) { cam.fov = f.fov; cam.updateProjectionMatrix(); }
      vcam.position.copy(f.pos);
      vcam.quaternion.copy(f.quat);
    }

    if (!ctx.viewScene.environment && ctx.scene.environment) {
      ctx.viewScene.environment = ctx.scene.environment;
      ctx.viewScene.environmentIntensity = ctx.scene.environmentIntensity;
    }

    ctx.scene.updateMatrixWorld();
    ctx.viewScene.updateMatrixWorld();
    if (cam.parent === null) cam.updateMatrixWorld(true); else cam.updateMatrixWorld(true);
    if (vcam.parent === null) vcam.updateMatrixWorld(true); else vcam.updateMatrixWorld(true);
    cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
    vcam.matrixWorldInverse.copy(vcam.matrixWorld).invert();

    if (jumped) {
      this.taa.resetHistory();
      this.exposure.requestSnap();
    }

    /* ---- jitter ---- */
    this._projSaved.copy(cam.projectionMatrix);
    this._projSavedView.copy(vcam.projectionMatrix);
    const useTaa = o.taa && !raw;
    let jx = 0, jy = 0;
    if (useTaa) {
      const j = this.jitterTable[this.frameIndex % this.jitterTable.length];
      jx = 2 * j[0] / this.width;
      jy = 2 * j[1] / this.height;
      cam.projectionMatrix.elements[8] += jx;
      cam.projectionMatrix.elements[9] += jy;
      vcam.projectionMatrix.elements[8] += jx;
      vcam.projectionMatrix.elements[9] += jy;
    }
    cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
    vcam.projectionMatrixInverse.copy(vcam.projectionMatrix).invert();

    this._proj.copy(cam.projectionMatrix);
    this._invProj.copy(cam.projectionMatrixInverse);
    this._projView.copy(vcam.projectionMatrix);
    this._invProjView.copy(vcam.projectionMatrixInverse);

    this._curVP.multiplyMatrices(this._projSaved, cam.matrixWorldInverse);
    this._curVPView.multiplyMatrices(this._projSavedView, vcam.matrixWorldInverse);
    if (jumped) {
      this._prevVP.copy(this._curVP);
      this._prevVPView.copy(this._curVPView);
    }

    /* ---- 1. geometry prepass (world) ---- */
    this.prepass.render(r, ctx.scene, cam, {
      curViewProj: this._curVP,
      prevViewProj: this._prevVP,
      flags: 0,
      clear: true,
      onMaterial: (m) => this._patchMaterial(m),
    });

    /* ---- 2. GTAO + screen-space contact shadows (one resolve pass) ---- */
    const sunDir = (this.rig.armed && this.rig.sunDirection)
      || (ctx.sky && ctx.sky.sunDirection) || null;
    const doContact = !!(o.contact && !raw && sunDir && sunDir.y > 0.01);
    if (doContact) {
      // World -> view. The sun direction is a direction, so only the rotation
      // of the view matrix applies; translation would put it in the wrong place
      // by the camera's own position.
      this._lightVS.copy(sunDir).transformDirection(cam.matrixWorldInverse).normalize();
    }
    if ((o.gtao || doContact) && !raw) {
      this.gtao.render(r, {
        ao: !!o.gtao,
        depthTexture: this.prepass.depthTexture,
        normalTexture: this.prepass.normalTexture,
        noiseTexture: this.blueNoise,
        noiseSize: this.noiseSize,
        invProj: this._invProj,
        proj11: this._proj.elements[5],
        radius: 1.1,
        intensity: 1.0,
        power: 2.2,
        frame: this.frameIndex,
        contact: doContact ? {
          proj: this._proj,
          lightVS: this._lightVS,
          strength: this.contact.strength,
          length: this.contact.length,
          thickness: this.contact.thickness,
          bias: this.contact.bias,
          fadeStart: this.contact.fadeStart,
          fadeEnd: this.contact.fadeEnd,
        } : null,
      });
      this.uAO.value = this.gtao.texture;
      this.uFlags.value.x = o.gtao ? 1 : 0;
      this.uFlags.value.w = o.gtao ? 0.45 : 0;
      this.uFlags2.value.x = doContact ? 1 : 0;
    } else {
      this.uAO.value = this.whiteTex;
      this.uFlags.value.x = 0;
      this.uFlags.value.w = 0;
      this.uFlags2.value.x = 0;
    }
    this.uFlags.value.y = (o.ssr && !raw) ? 1 : 0;
    this.uFlags.value.z = 1.0;

    /* ---- 3. forward HDR colour pass ----
       Every shadow caster must own a rendered map before we submit a single
       shadow-receiving material, or ANGLE discards the draw outright. */
    this._ensureShadowMaps(ctx, jumped);

    const prevAutoClear = r.autoClear;
    r.autoClear = false;
    r.setRenderTarget(this.hdrRT);
    r.setClearColor(0x000000, 1);
    r.clear(true, true, false);
    r.render(ctx.scene, cam);

    /* ---- 4. volumetric composite ---- */
    const volTex = ctx.lighting && ctx.lighting.volumetricTexture;
    if (o.volumetric && volTex && volTex.image) {
      this.volumetric.render(r, this.hdrRT, {
        volumetricTexture: volTex,
        dataTexture: this.prepass.dataTexture,
        intensity: this.volumetricIntensity
          * (ctx.lighting.volumetricIntensity ?? ctx.lighting.grading?.volumetricIntensity ?? 1.0),
        transmittance: !!ctx.lighting.volumetricHasTransmittance,
      });
    }

    /* ---- 5. SSR raymarch (consumed by the next frame's forward pass) ---- */
    if (o.ssr && !raw) {
      this.ssr.render(r, {
        colorTexture: this.hdrRT.texture,
        depthTexture: this.prepass.depthTexture,
        normalTexture: this.prepass.normalTexture,
        noiseTexture: this.blueNoise,
        noiseSize: this.noiseSize,
        proj: this._proj,
        invProj: this._invProj,
        near: cam.near,
        frame: this.frameIndex,
        intensity: 1.0,
      });
      this.uSSR.value = this.ssr.texture;
    } else {
      this.uSSR.value = this.zeroTex;
    }

    /* ---- 6. viewmodel: own depth, own occlusion, own motion vectors ----
       The viewmodel prepass clears depth, so from here the depth buffer holds
       nothing but the weapon. Its AO and contact shadow are therefore computed
       against a buffer that is ~90 % sky, and both kernels take the sky
       early-out, which is why a second full-res occlusion pass costs almost
       nothing. Without it the optic casts nothing onto the receiver 3 cm below
       it and the rail slots have no darkening at all — the loudest tell in
       every weapon frame this build has produced. */
    if (ctx.viewScene.children.length > 0) {
      this.prepass.render(r, ctx.viewScene, vcam, {
        curViewProj: this._curVPView,
        prevViewProj: this._prevVPView,
        flags: 1,
        clear: false,
        onMaterial: (m) => this._patchMaterial(m, true),
      });
      const vmOcc = o.gtao && !raw;
      const vmContact = doContact;
      if (vmOcc || vmContact) {
        this.gtao.render(r, {
          ao: vmOcc,
          depthTexture: this.prepass.depthTexture,
          normalTexture: this.prepass.normalTexture,
          noiseTexture: this.blueNoise,
          noiseSize: this.noiseSize,
          invProj: this._invProjView,
          proj11: this._projView.elements[5],
          // A 60 cm object at 30 cm needs a hemisphere radius measured in
          // centimetres; the world's 1.1 m radius reaches past the whole weapon
          // and returns a flat 1.0 everywhere.
          radius: 0.055,
          intensity: 1.0,
          power: 1.7,
          frame: this.frameIndex,
          contact: vmContact ? {
            proj: this._projView,
            lightVS: this._lightVS,
            strength: this.contactVm.strength,
            length: this.contactVm.length,
            thickness: this.contactVm.thickness,
            bias: this.contactVm.bias,
            fadeStart: this.contactVm.fadeStart,
            fadeEnd: this.contactVm.fadeEnd,
          } : null,
        }, this.gtao.vmRT);
        this.uAO.value = this.gtao.viewmodelTexture;
      } else {
        this.uAO.value = this.whiteTex;
      }
      this.uFlags.value.x = 0;
      this.uFlags.value.y = 0;
      this.uFlags.value.w = 0;
      this.uFlags2.value.y = vmContact ? 1 : 0;
      this.uFlags2.value.z = vmOcc ? 0.85 : 0;
      r.setRenderTarget(this.hdrRT);
      r.clearDepth();
      r.render(ctx.viewScene, vcam);
    }
    r.autoClear = prevAutoClear;

    /* ---- 7. antialias resolve ---- */
    let colorTex = this.hdrRT.texture;
    if (useTaa) {
      colorTex = this.taa.render(r, {
        colorTexture: this.hdrRT.texture,
        dataTexture: this.prepass.dataTexture,
        alpha: 0.09,
        sharpness: 0.72,
      });
    } else if (o.smaa && this.smaa && !raw) {
      this.smaa.renderToScreen = false;
      this.smaa.render(r, this.smaaRT, this.hdrRT);
      colorTex = this.smaaRT.texture;
    }

    /* ---- 8. motion blur ---- */
    if (o.mblur && !raw && !jumped) {
      colorTex = this.motionBlur.render(r, {
        colorTexture: colorTex,
        dataTexture: this.prepass.dataTexture,
        noiseTexture: this.blueNoise,
        noiseSize: this.noiseSize,
        shutter: 0.55,
        frame: this.frameIndex,
      });
    }

    /* ---- 9. depth of field ---- */
    if (o.dof && !raw) {
      const ads = clamp(this._adsOverride !== null && this._adsOverride !== undefined
        ? this._adsOverride
        : ((ctx.weapons && ctx.weapons.ads) || 0), 0, 1);
      this._ads += (ads - this._ads) * 0.35;
      const a = this._ads;
      /* Everything is a fraction of the metered focal plane, so one set of
         numbers works whether the subject is a squad at 25 m or a sight at
         40 cm.

         Hip fire is a deep-but-not-infinite lens: sharp from ~30 % of the focal
         distance to ~2.6x it, then a bounded 5 px rolloff. That keeps the
         playable band and the roofline readable — the previous 800 m / 4000 m
         numbers were a real fix for the sky-as-near-field bug, but they removed
         the artefact by removing the effect, and three of the four best shipped
         frames in refs/ lean on exactly this.

         ADS collapses the band onto the sights and lets the lane fall away. */
      colorTex = this.dof.render(r, {
        colorTexture: colorTex,
        dataTexture: this.prepass.dataTexture,
        noiseTexture: this.blueNoise,
        noiseSize: this.noiseSize,
        dt: ctx.time.dt || 1 / 60,
        snap: jumped || !!(ctx.engine && ctx.engine.deterministic),
        focusOverride: this._focusOverride,
        focusSpread: THREE.MathUtils.lerp(0.26, 0.10, a),
        focusMin: 1.2,
        focusMax: THREE.MathUtils.lerp(48.0, 90.0, a),
        nearFrac: THREE.MathUtils.lerp(0.30, 0.62, a),
        nearRoll: THREE.MathUtils.lerp(0.90, 0.45, a),
        farFrac: THREE.MathUtils.lerp(2.60, 1.25, a),
        farRoll: THREE.MathUtils.lerp(4.00, 5.00, a),
        // A carried weapon is a touch soft at hip fire, not mush: at -0.26 the
        // half-res near gather was wide enough to show its own tap pattern.
        vmCoc: THREE.MathUtils.lerp(-0.10, 0.0, a),
        maxCocPixels: THREE.MathUtils.lerp(5.0, 10.0, a) * (this.height / 1080),
        frame: this.frameIndex,
      });
    }

    /* ---- 10. auto exposure ---- */
    this._lastColorTex = colorTex;
    this.exposure.render(r, {
      colorTexture: colorTex,
      srcWidth: this.width,
      srcHeight: this.height,
      dt: ctx.time.dt || 1 / 60,
      manual: this._manualExposure,
      /* The frame is metered *after* the anchor has been applied to it, so the
         value that comes out is the residual — how far the render is from what
         the lighting model predicted — and not the absolute exposure. */
      preExposure: this._baseExposure,
      key: this._exposureKey,
      /* The capture harness advances a fixed number of steps and then reads the
         framebuffer; an eye-adaptation time constant measured in seconds does not
         converge inside that budget, and half the shots came out as blank white
         or blank black rectangles because of it. In deterministic mode there is
         no eye to adapt, so the histogram result is taken directly. Gameplay
         still gets the smoothed adaptation. */
      snap: this._forceConverge || !!(ctx.engine && ctx.engine.deterministic),
      /* The histogram may correct the anchor by +/- AE_STOPS and no more. The
         buffer is already pre-exposed by the anchor, so the window is centred on
         1.0 rather than on the anchor's own value. A wide window let
         auto-exposure silently absorb ~2 stops of missing key light and present
         it as a milky veil; +/- 1.5 stops is enough for the framing variation a
         position-based anchor cannot predict (measured: 1.06 stops across the
         six acceptance shots) and not enough to hide a lighting bug. */
      minExposure: 1 / AE_RANGE,
      maxExposure: AE_RANGE,
    });
    this._forceConverge = false;

    /* ---- 11. bloom ---- */
    let bloomTex = this.zeroTex;
    let streakTex = this.zeroTex;
    if (!raw) {
      this.bloom.render(r, {
        colorTexture: colorTex,
        exposureTexture: this.exposure.texture,
        preExposure: this._baseExposure,
        threshold: 1.05,
        knee: 0.62,
        radius: 1.0,
        streak: o.streak ? 1 : 0,
      });
      bloomTex = this.bloom.texture;
      streakTex = o.streak ? this.bloom.streakTexture : this.zeroTex;
    }

    /* ---- 12. display ---- */
    const cu = this.composite.mat.uniforms;
    cu.uBloom.value = raw ? 0 : 0.045;
    cu.uStreak.value = o.streak && !raw ? 0.22 : 0;
    cu.uGrade.value = raw ? 0 : 1;
    /* Lens character, per CRITIC.md 5: each term individually almost invisible,
       all of them together the difference between "a render" and "a photograph
       of something". 0.00024 was below the threshold of *existing* — a blind
       critic scored eight frames of this build and reported "no aberration" on
       all of them. 0.00055 is ~1.0 px of R-B separation in the extreme corner
       and still nothing measurable across the middle two thirds. */
    cu.uCA.value = raw ? 0 : 0.00055;
    cu.uDistort.value = raw ? 0 : 0.010;
    // See Composite.js :: uVignette — 0.34 was eating 22% off the upper third of
    // every exterior frame, which is where the only near-white content is.
    cu.uVignette.value = raw ? 0 : 0.20;
    // Same story: 0.006 is ±0.6 % on an 8-bit frame, i.e. ±1.5 codes, which is
    // the dither floor rather than grain. 0.013 shadow-weighted is still under
    // 1 % in the midtones and reads as film stock rather than as noise.
    cu.uGrain.value = raw ? 0 : 0.013;
    cu.uFlashPower.value = this._flash.power;
    cu.uFlashColor.value.set(this._flash.color.r, this._flash.color.g, this._flash.color.b);
    cu.uDamage.value = raw ? 0 : Math.min(1, this._damage);

    if (this.debugView !== 'none') {
      const dm = this.debugMat.uniforms;
      const map = {
        ao: [this.gtao.texture, 1, 1],
        ssr: [this.ssr.rtA ? this.ssr.texture : this.zeroTex, 2, 6],
        ssrconf: [this.ssr.rtA ? this.ssr.texture : this.zeroTex, 3, 1],
        normal: [this.prepass.normalTexture, 4, 1],
        velocity: [this.prepass.dataTexture, 5, 1],
        depth: [this.prepass.dataTexture, 6, 1],
        flags: [this.prepass.dataTexture, 7, 1],
        bloom: [bloomTex, 0, 4],
        hdr: [this.hdrRT.texture, 0, 1],
      };
      const e = map[this.debugView];
      if (e) {
        dm.tDebug.value = e[0]; dm.uMode.value = e[1]; dm.uScale.value = e[2];
        this.blit.draw(r, this.debugMat, null);
        r.setRenderTarget(null);
        this._restoreFrame(ctx, cam, vcam, shake);
        this.stats.cpuMs = ((typeof performance !== 'undefined') ? performance.now() : 0) - t0;
        return;
      }
    }

    this.composite.render(r, null, {
      colorTexture: colorTex,
      bloomTexture: bloomTex,
      streakTexture: streakTex,
      dirtTexture: this.lensDirt,
      exposureTexture: this.exposure.texture,
      preExposure: this._baseExposure,
      noiseTexture: this.blueNoise,
      noiseSize: this.noiseSize,
      width: this.width,
      height: this.height,
      frame: this.frameIndex,
    });

    r.setRenderTarget(null);

    /* ---- restore everything we borrowed ---- */
    this._restoreFrame(ctx, cam, vcam, shake);

    this.prepass.gc();
    this.stats.cpuMs = ((typeof performance !== 'undefined') ? performance.now() : 0) - t0;
  }

  _restoreFrame(ctx, cam, vcam, shake) {
    this._prevVP.copy(this._curVP);
    this._prevVPView.copy(this._curVPView);
    cam.projectionMatrix.copy(this._projSaved);
    cam.projectionMatrixInverse.copy(this._projSaved).invert();
    vcam.projectionMatrix.copy(this._projSavedView);
    vcam.projectionMatrixInverse.copy(this._projSavedView).invert();

    if (this._forcedCam) {
      /* Leave the pin in place rather than handing the camera back. Systems
         that run before us next frame (lighting's volumetric march, the weapon
         rig) read ctx.camera to place their own work; if we only held the pose
         inside render() they would all be one shot behind and composite for a
         camera that is not the one being drawn. */
      const f = this._forcedCam;
      cam.position.copy(f.pos);
      cam.quaternion.copy(f.quat);
      vcam.position.copy(f.pos);
      vcam.quaternion.copy(f.quat);
    } else {
      cam.position.copy(this._savedPos);
      cam.quaternion.copy(this._savedQuat);
      vcam.position.copy(this._savedPosView);
      vcam.quaternion.copy(this._savedQuatView);
    }
    if (shake.active || this._forcedCam) { cam.updateMatrixWorld(true); vcam.updateMatrixWorld(true); }
  }

  /* ---------------------------------------------------------------- */

  /** Hand the camera (and the player) back after one of our shots. */
  _releaseShot(ctx) {
    if (this.rig.armed) this.rig.disarm(ctx);
    this._rawMode = false;
    this._adsOverride = null;
    this._focusOverride = 0;
    if (this._forcedCam) {
      this._forcedCam = null;
      const p = ctx.get && ctx.get('player');
      if (p && typeof p.freeze === 'function') p.freeze(false);
    }
  }

  _armRig(ctx) {
    this.rig.ensureEnv(ctx.renderer);
    this.rig.arm(ctx);
    // The rig's own casters have just entered the scene with no rendered map.
    this._ensureShadowMaps(ctx, true);
    this._rigArmedFrame = ctx.time.frame;
    this.taa.resetHistory();
    this.exposure.requestSnap();
  }

  /** Common state reset + camera pin for every render-team shot. */
  _prepareShot(ctx, opts) {
    this._rawMode = !!opts.raw;
    this._damage = 0;
    this._flash.power = 0;
    this._shakes.length = 0;
    this._manualExposure = 0;
    if (ctx.weapons) ctx.weapons.ads = opts.ads || 0;
    this._adsOverride = (opts.ads === undefined) ? null : opts.ads;
    this._focusOverride = (opts.focus > 0) ? opts.focus : 0;
    this.dof.requestSnap();
    this._ads = opts.ads || 0;
    // other teams' shots move the sun; pin it so ours are reproducible
    if (ctx.lighting && typeof ctx.lighting.setTimeOfDay === 'function') {
      try { ctx.lighting.setTimeOfDay(opts.tod !== undefined ? opts.tod : TOD.golden); } catch (e) { /* lighting may be a stub */ }
    }
    const player = ctx.get && ctx.get('player');
    if (player && typeof player.freeze === 'function') player.freeze(true);
    if (opts.rig) this._armRig(ctx);
    else if (this.rig.armed) this.rig.disarm(ctx);
    /* The stand-in viewmodel is opt-in only: the weapons team owns the real one,
       and a placeholder in a hero shot is worse than no weapon at all. It stays
       available (opts.viewmodel === true, or ctx.render.probeRig) so the
       viewmodel half of the DOF / velocity path can be exercised on demand. */
    if (this.rig.viewRoot) {
      this.rig.viewRoot.visible = opts.viewmodel === true && !this.rig.realViewmodelInView(ctx);
    }
    this.rig.setSsrProbe(!!opts.ssrProbe);
    this._rigArmedFrame = ctx.time.frame;
    this.taa.resetHistory();
    this.exposure.requestSnap();
  }

  _pin(ctx, fov) {
    const cam = ctx.camera;
    this._forcedCam = {
      pos: cam.position.clone(),
      quat: cam.quaternion.clone(),
      fov,
    };
    /* Drive the player to the same pose. Everything downstream of the player
       system — the weapon viewmodel rig, spatial audio, the lighting team's
       volumetric march — reads ctx.camera *before* we get the frame, so a pin
       that only lived inside render() would leave the weapon and the shafts
       placed for wherever the player happened to be standing. */
    const p = ctx.player;
    if (p && typeof p.teleport === 'function') {
      const e = _euler.setFromQuaternion(cam.quaternion, 'YXZ');
      const eye = p.eyeHeight || 1.65;
      try {
        p.teleport(_vec.set(cam.position.x, cam.position.y - eye, cam.position.z), e.y);
        if (typeof p.setLook === 'function') p.setLook(e.y, e.x);
      } catch (err) { /* player may still be a stub */ }
    }
  }

  _registerShots(ctx) {
    const self = this;
    const shot = (name, from, at, fov, opts = {}) => {
      registerShot(name, (c) => {
        self._prepareShot(c, opts);
        look(c, from, at, fov);
        self._pin(c, fov);
      }, { desc: opts.desc, settle: opts.settle || 30 });
    };

    /* The reference pair is the same camera twice: full chain vs tonemap only.
       Framed down the centre lane of BLACKOUT: KHALIJ at player eye height. */
    const REF_FROM = [0.4, 1.66, 13.2];
    const REF_AT = [-0.6, 2.15, -24.0];

    shot('post_reference', REF_FROM, REF_AT, 74, {
      desc: 'full post chain — the A side of the A/B against post_raw',
    });
    shot('post_raw', REF_FROM, REF_AT, 74, {
      raw: true,
      desc: 'identical camera, AgX tonemap only — no AO/SSR/TAA/DOF/MB/bloom/grade',
    });

    shot('post_bloom', [20.2, 1.60, 0.4], [12.5, 1.55, -6.6], 74, {
      desc: 'dark souk interior looking out a blown shopfront — bloom, streak, lens dirt',
    });

    shot('post_ssr', [-0.4, 0.62, 6.4], [-1.2, 1.35, -12.0], 66, {
      ssrProbe: true,
      desc: 'grazing angle over standing water and shopfront glass — SSR',
    });

    shot('post_gtao', [-21.6, 1.10, -10.9], [-20.2, 0.20, -13.4], 46, {
      desc: 'alley wall base and rubble — contact occlusion',
    });

    shot('post_dof_ads', REF_FROM, REF_AT, 60, {
      ads: 1, focus: 6.5,
      desc: 'ctx.weapons.ads = 1 — sights sharp, the lane behind them falls off',
      settle: 34,
    });
    shot('post_dof_hip', REF_FROM, REF_AT, 74, {
      ads: 0, focus: 26,
      desc: 'the ads = 0 counterpart of post_dof_ads',
      settle: 34,
    });

    /* Controlled probe scene — kept so the pipeline stays testable if the level
       is ever unavailable, and so material response can be judged in isolation. */
    shot('post_probe', [7.4, 1.75, 6.6], [-2.6, 1.5, -3.4], 62, {
      rig: true,
      desc: 'render-team probe rig: wet asphalt, steel, glass, emissive signage',
    });

    void ctx;
  }

  /* ---------------------------------------------------------------- */

  dispose() {
    if (this.hdrRT) this.hdrRT.dispose();
    if (this.smaaRT) this.smaaRT.dispose();
    this.prepass.dispose(); this.gtao.dispose(); this.ssr.dispose(); this.taa.dispose();
    this.bloom.dispose(); this.dof.dispose(); this.motionBlur.dispose();
    this.volumetric.dispose(); this.exposure.dispose(); this.composite.dispose();
    this.rig.dispose();
    this.blit.dispose();
  }
}

export function createRenderSystem() {
  return new RenderSystem();
}

export { PROBE_LAYER };
